import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { XCircleIcon } from '@heroicons/react/outline';

interface Settings {
  homepageVideo?: {
    url: string;
    fileName: string;
  };
}

const SettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch settings on component mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/settings');
        
        if (response.data.success) {
          setSettings(response.data.data);
        } else {
          setError('Failed to load settings');
        }
      } catch (err: any) {
        setError(
          err.response?.data?.error || 
          'An error occurred while fetching settings'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Handle video file upload
  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const formData = new FormData();
    formData.append('video', file);

    try {
      setUploading(true);
      setUploadSuccess(false);
      setError(null);

      const response = await axios.put('/api/settings/homepage-video', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setSettings(response.data.data);
        setUploadSuccess(true);
        
        // Reset the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error || 
        'An error occurred while uploading the video'
      );
    } finally {
      setUploading(false);
    }
  };

  // Handle video deletion
  const handleDeleteVideo = async () => {
    try {
      setUploading(true);
      setDeleteSuccess(false);
      setError(null);

      const response = await axios.delete('/api/settings/homepage-video');

      if (response.data.success) {
        setSettings(prev => ({
          ...prev as Settings,
          homepageVideo: undefined
        }));
        setDeleteSuccess(true);
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error || 
        'An error occurred while deleting the video'
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6 text-foreground dark:text-dark-foreground">Site Settings</h1>
      
      {error && (
        <div className="bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded-xl mb-4 text-sm font-medium">
          {error}
        </div>
      )}
      
      {uploadSuccess && (
        <div className="bg-success/10 border border-success/30 text-success px-4 py-3 rounded-xl mb-4 text-sm font-medium">
          Homepage video uploaded successfully.
        </div>
      )}
      
      {deleteSuccess && (
        <div className="bg-success/10 border border-success/30 text-success px-4 py-3 rounded-xl mb-4 text-sm font-medium">
          Homepage video deleted successfully.
        </div>
      )}
      
      <div className="bg-card dark:bg-dark-card border border-border/60 dark:border-dark-border/60 rounded-2xl shadow-sm p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4 text-foreground dark:text-dark-foreground">Homepage Video</h2>
        
        {loading ? (
          <p>Loading settings...</p>
        ) : (
          <>
            <div className="space-y-4">
              {settings?.homepageVideo ? (
                <div className="space-y-4">
                  <div className="mt-2">
                    <h3 className="text-sm font-medium text-foreground dark:text-dark-foreground">Current Video</h3>
                    <div className="mt-2 max-w-2xl">
                      <video 
                        src={settings.homepageVideo.url}
                        controls
                        className="w-full rounded-xl border border-border/40 dark:border-dark-border/40"
                      />
                      <p className="mt-2 text-xs text-foreground/60 dark:text-dark-foreground/60">
                        {settings.homepageVideo.fileName}
                      </p>
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    onClick={handleDeleteVideo}
                    disabled={uploading}
                    className="inline-flex items-center px-4 py-2 border border-destructive/20 rounded-full shadow-sm text-xs font-semibold uppercase tracking-wide text-destructive bg-destructive/10 hover:bg-destructive hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive disabled:opacity-50"
                  >
                    <XCircleIcon className="-ml-1 mr-2 h-4 w-4" aria-hidden="true" />
                    {uploading ? 'Deleting...' : 'Delete Video'}
                  </button>
                </div>
              ) : (
                <p className="text-sm text-foreground/60 dark:text-dark-foreground/60">No homepage video uploaded yet.</p>
              )}
              
              <div className="mt-6 pt-6 border-t border-border/30 dark:border-dark-border/30">
                <label htmlFor="video-upload" className="block text-sm font-medium text-foreground dark:text-dark-foreground">
                  {settings?.homepageVideo ? 'Replace Video' : 'Upload Video'}
                </label>
                <div className="mt-3 flex items-center">
                  <input
                    id="video-upload"
                    name="video"
                    type="file"
                    accept="video/mp4,video/webm"
                    onChange={handleVideoUpload}
                    ref={fileInputRef}
                    disabled={uploading}
                    className="block w-full text-sm text-foreground/60 dark:text-dark-foreground/60 file:mr-4 file:py-2.5 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:uppercase file:tracking-wide file:bg-secondary file:text-foreground hover:file:bg-secondary/80 dark:file:bg-dark-secondary dark:file:text-dark-foreground transition-all cursor-pointer"
                  />
                  {uploading && (
                    <div className="ml-3">
                      <svg className="animate-spin h-5 w-5 text-accent" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                </div>
                <p className="mt-3 text-xs text-foreground/50 dark:text-dark-foreground/50 font-light">
                  Upload an MP4 or WebM video file (max 10MB).
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SettingsPage; 